
import { useState, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { Upload, X, Loader2, Image as ImageIcon } from 'lucide-react';

interface ImageUploadProps {
    value: string | null;
    onChange: (url: string) => void;
    bucket?: string;
    folder?: string;
    multiple?: boolean;
}

export default function ImageUpload({
    value,
    onChange,
    bucket = 'portfolio-assets',
    folder = 'projects',
    multiple = false
}: ImageUploadProps) {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        try {
            setError(null);
            setUploading(true);

            if (!event.target.files || event.target.files.length === 0) {
                throw new Error('You must select at least one image to upload.');
            }

            const files = Array.from(event.target.files);

            for (const file of files) {
                const fileExt = file.name.split('.').pop();
                const fileName = `${folder}/${Math.random().toString(36).substring(2)}.${fileExt}`;
                const filePath = `${fileName}`;

                // Upload to Supabase
                const { error: uploadError } = await supabase.storage
                    .from(bucket)
                    .upload(filePath, file);

                if (uploadError) {
                    throw uploadError;
                }

                // Get Public URL
                const { data } = supabase.storage
                    .from(bucket)
                    .getPublicUrl(filePath);

                onChange(data.publicUrl);
            }
        } catch (error: any) {
            console.error('Error uploading image:', error);
            setError(error.message || 'Error uploading image');
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const clearImage = () => {
        onChange('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div className="w-full">
            <label className="block text-sm font-medium text-gray-400 mb-2">
                Project Image
            </label>

            {value ? (
                <div className="relative aspect-video w-full rounded-lg overflow-hidden border border-gray-700 group">
                    <img
                        src={value}
                        alt="Uploaded preview"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <button
                            type="button"
                            onClick={clearImage}
                            className="p-2 bg-red-600 text-white rounded-full hover:bg-red-500 transition-colors"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>
            ) : (
                <div
                    onClick={() => fileInputRef.current?.click()}
                    className={`
            relative border-2 border-dashed border-gray-700 rounded-lg p-8
            flex flex-col items-center justify-center cursor-pointer
            hover:border-blue-500 hover:bg-gray-800/50 transition-all
            ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
          `}
                >
                    {uploading ? (
                        <Loader2 className="h-10 w-10 text-blue-500 animate-spin mb-3" />
                    ) : (
                        <Upload className="h-10 w-10 text-gray-500 mb-3" />
                    )}
                    <p className="text-sm text-gray-400 font-medium">
                        {uploading ? 'Uploading...' : 'Click to upload image'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                        PNG, JPG, GIF up to 5MB
                    </p>
                </div>
            )}

            <input
                type="file"
                ref={fileInputRef}
                onChange={handleUpload}
                accept="image/*"
                className="hidden"
                disabled={uploading}
                multiple={multiple}
            />

            {error && (
                <p className="mt-2 text-sm text-red-400">{error}</p>
            )}
        </div>
    );
}
